import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import PresetModal from "../../UI/Modal/PresetModal/PresetModal";
import * as actionTypes from "../../../store/actions/actionTypes";
import styles from './MergeSelectionModal.module.css'
import apiUrl from "../../../shared/config";
import SimpleSearchWrapper from "../../SimpleSearchWrapper/SimpleSearchWrapper";
import ManualSearchBar from "../../ManualSearchBar/ManualSearchBar";
import Pattern from "../../Pattern/Pattern";
import classes from "../../Pattern/Pattern.module.css";
import {AnimatePresence, motion} from "framer-motion";
import {enterRightExitLeft} from "../../../utils/commonAnimations";
import AddPatternComment from "../../PatternComment/AddPatternComment/AddPatternComment";
import './InformationalList.css'
import InfoButton from "../../IconButtons/InfoButton/InfoButton";

const CurrentPage = {
    SELECT_PATTERN: 0,
    SIMPLE_WARNING_PAGE: 1,
    // more advanced warning pages here in the future.
}

export default function MergeSelectionModal() {
    const [selected, setSelected] = useState({id: -1, name: null})
    const [currentPage, setCurrentPage] = useState(CurrentPage.SELECT_PATTERN)
    // const [isLoading, setIsLoading] = useState(false) // should be true when it gets used for the search function.
    const to = useSelector(state => state.modal.merge_selection_modal.selectedTo)
    const from = useSelector(state => state.modal.merge_selection_modal.selectedFrom)
    const show = useSelector(state => state.modal.merge_selection_modal.isOpen)
    const userId = useSelector(state => state.auth.userId)
    const token = useSelector(state => state.auth.token)
    const modalRef = useRef();
    const submitCommentRef = useRef();
    const dispatch = useDispatch();

    const searchMapFunction = (data) => {
        return {
            count: data.pattern_count || 0,
            arr: data.patterns_array.map(pattern => {
                return <Pattern
                        chosenPattern={+selected.id === +pattern.pattern_id}
                        key={pattern.pattern_id}
                        name={pattern.pattern_name}
                        cardBodyText={pattern.pattern_descriptive_title !== '' ? pattern.pattern_descriptive_title : pattern.pattern_design_problem}
                        cardBodyStyle={pattern.pattern_descriptive_title !== '' ? "descriptive-title-card-text" + classes.truncate : "cardText " + classes.truncate}
                        clicked={() => {
                            setSelected({id: pattern.pattern_id, name: pattern.pattern_name})
                        }}
                    />
            })
        }
    }

    useEffect(() => {
        if (modalRef.current && show) {
            modalRef.current.open();
        }
    }, [show]);

    const mergeButtonStyle = {
        backgroundColor: (selected.id < 0 && currentPage === CurrentPage.SELECT_PATTERN) && 'darkgray',
        borderColor: (selected.id < 0 && currentPage === CurrentPage.SELECT_PATTERN) && 'darkgray'
    }

    function mergeButtonHandler() {
        if (selected.id < 0) {
            // might want to put warning here, but I am not sure if its worth it. For now do nothing
            return;
        }
        switch (currentPage) {
            case CurrentPage.SELECT_PATTERN:
                setCurrentPage(CurrentPage.SIMPLE_WARNING_PAGE)
                break;
            case CurrentPage.SIMPLE_WARNING_PAGE:
                submitCommentRef && submitCommentRef.current.sendComment()
                const postTo = to && to.id ? to : {id: selected.id, name: selected.name};
                const postFrom = from && from.id ? from : {id: selected.id, name: selected.name};

                dispatch({type: actionTypes.SUBMIT_MERGE_SELECT_MODAL, to: postTo, from: postFrom})
                setSelected({id: -1, name: null})
                setCurrentPage(CurrentPage.SELECT_PATTERN)
                modalRef.current && modalRef.current.close();
                break;
            default:
                console.warn('You should never get here so I am gonna send you back to the patterns selection page')
                setCurrentPage(CurrentPage.SELECT_PATTERN);
        }
    }

    const mergeText = to && to.id ? 'Merge "' + String(selected.name || '').trim() + '" with "' + String(to.name).trim() + '"?'
        : 'Merge "' + String(from.name || '').trim() + '" with "' + String(selected.name).trim() + '"?'

    const defaultCommentText = 'Hi, I believe that both of our patterns tackle similar problems in the same way. ' +
        'I am going to create a merged pattern so we can combine our ideas and collaborate going forwards.'

    const modalContent = (to && to.id) || (from && from.id) ?
        (<div style={{height: 790, overflowX: "hidden"}}>
            <AnimatePresence>
                {currentPage === CurrentPage.SELECT_PATTERN &&
                    <motion.div variants={enterRightExitLeft}
                                initial={'open'} // if we ever have a back button it might be worth using a has mounted so the animation will play
                                animate={'open'}
                                exit={'exit'}
                                style={{
                                    position: "absolute",
                                    width: '100%',
                                    top: 0,
                                    height: 770,
                                    marginBlock: 0,
                                    overflow: 'hidden'
                                }}
                                className={'card pattern-details'}
                    >
                        <h2 className={`card-header ${styles.ExtraCardHeaderStyles}`}>
                            {selected.id > 0 ? mergeText :
                                (to && to.id ? `Choose A Pattern To Merge With "${String(to.name || 'this pattern').trim()}"`
                                    : `Choose A Pattern To Merge "${String(to.name || 'this pattern').trim()}" With`)}
                        </h2>
                        {/*Note that quantity and offset are handled in the wrapper*/}
                        <div style={{marginTop: 10}}>
                            <SimpleSearchWrapper
                                endpoint={apiUrl + 'PatternList/getList?token=' + token + '&pattern_name_filter='}
                                dataMapFunction={searchMapFunction}
                                filterByUserId={from && from.id && userId ? userId : null}
                                title={<div style={{textAlign: "left", marginTop: -10, marginBottom: 6, fontWeight: "bold"}}>Merge Pattern Search:</div>}
                            >
                                <ManualSearchBar placeholder={'Search for the name of the pattern you want to merge with.'}/>
                            </SimpleSearchWrapper>
                            <div className={styles.MarginBottomDiv}></div>
                        </div>
                    </motion.div>}
            </AnimatePresence>

                <AnimatePresence>
                {currentPage === CurrentPage.SIMPLE_WARNING_PAGE &&
                <motion.div variants={enterRightExitLeft}
                            initial={'initial'}
                            animate={'open'}
                            exit={'exit'}
                            style={{position: "absolute", width: '100%', top: 0, height: 770, marginBlock: 0, overflow: 'hidden'}}
                            className={'card pattern-details'}
                >
                    <h2 className={`card-header ${styles.ExtraCardHeaderStyles}`}>{mergeText}</h2>
                    <div className={`${styles.Container}`}>
                        <div className={'informational-list-container'}>
                            <ul className={'informational-list'}>
                                <li>Merging these patterns will create a new pattern.</li>
                                <li>Authorship, credit, and edit history of all pattern components will be preserved on
                                    the new pattern.
                                </li>
                                <li>{`All authors of ${String(from.name || selected.name || 'this pattern').trim()} will be added as authors to the new pattern when the merge is complete.`}</li>
                                <li>All groups will be automatically added to the new pattern, but you will be able to
                                    remove any groups you are a member of.
                                </li>
                                <li>If the new merged pattern has the same title as one of the old patterns used for the
                                    merge then the old pattern with the same name will have a unique merged tag appended
                                    to the front of the pattern title.
                                </li>
                                {/*<li>Once your old pattern will be placed in a merged state.</li>*/}
                            </ul>
                        </div>
                        <div className={'informational-list-container'}>
                            <div className={`${styles.Title}`}>
                                {`Message to Pattern Authors:`}
                                <InfoButton infoContent={{text: `When you click the Merge Patterns the message below will be sent to the authors of the pattern ${String(from.name || selected.name || 'this pattern').trim()}`}}/>
                            </div>
                            <AddPatternComment ref={submitCommentRef} pattern_id={selected.id} userId={userId} token={token}
                                               alwaysShow
                                               update={() => {
                                               }}
                                               startingText={defaultCommentText}
                                               hideButton
                            />
                        </div>
                    </div>
                </motion.div>}
                </AnimatePresence>
            </div>
        )
        : <div className={styles.Title}>An error has occurred, please try again later.</div>

    return <PresetModal ref={modalRef}
                        onClose={() => {
                            document.body.style.overflow = ''; // felt like this should be handled in preset modal, but it doesn't seem to work for some reason here.
                            setSelected({id: -1, name: null})
                            setCurrentPage(CurrentPage.SELECT_PATTERN)
                            dispatch({type: actionTypes.CLOSE_MERGE_SELECT_MODAL})
                        }}
                        startingState={currentPage > 0}
                        buttonText={'Merge Patterns'}
                        onButtonPress={mergeButtonHandler}
                        buttonStyle={mergeButtonStyle}
                        hideXOverflow
    >
        <div style={{width: 2000}}></div> {/* This feels like cheating, but it actually seems to work very well. */}
        {modalContent}
    </PresetModal>
}